import { useNavigate } from "react-router-dom"
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Slide,
  Fab,
  useTheme,
  Paper,
  CardMedia,
} from "@mui/material"
import _ from "lodash"
import { Trans } from "react-i18next"

import { isValidURL } from "utils/url"
import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useStepByStep } from "hooks/direction/useStepByStep"
import { useCallback, useMemo, useRef, useState } from "react"
import { useStepByStepMap } from "hooks/direction/useStepByStepMap"
import { useStepTracking } from "hooks/analytics"
import { sanitizePublicPath } from "providers/venue/utils/fetchUtils"

import { TopBarContainer } from "containers/TopBarContainer"
import {
  BackIcon,
  PrevStepIcon,
  NextStepIcon,
  PauseIcon,
  RefreshArrowIcon,
  PlayArrowIcon,
} from "components/icons"
import { VerticalDirectionImageDialog } from "components/Dialog/VerticalDirectionImageDialog"
import { StepProgressBar } from "components/Progress/StepProgressBar"
import { OrdinalSelector } from "components/OrdinalSelector"
import { CoolDownProgress } from "extensions/iconsiam/components/CoolDownProgress"

export const StepByStep = () => {
  const navigate = useNavigate()
  const { isMultiOrdinalVenue } = useVenue()
  const contentRef = useRef(null)
  const [slideEntered, setSlideEntered] = useState(false)
  const offsetBottom = +contentRef.current?.clientHeight || 0
  const {
    route,
    steps,
    currentStepNumber,
    currentStep,
    currentOriginStepLevelName,
    currentDestStepLevelName,
    currentLevel,
    currentOrdinal,
    originName,
    originVenueLogo,
    currentVerticalStepMethod,
    destinationName,
    destinationVenueLogo,
    isReady,
    hasNextStep,
    hasPrevStep,
    isPlaying,
    stepAutoplayConfig,
    goToStepByIndex,
    goToPrevStep,
    goToNextStep,
    restartAutoPlay,
    resumeAutoPlay,
    pauseAutoPlay,
    directionOverviewPath,
  } = useStepByStep()
  const stepsAvailable = steps && steps.length > 0
  useStepByStepMap(route, currentStepNumber, {
    offset: { bottom: offsetBottom, top: 0, left: 0, right: 0 },
    enabled: isReady && slideEntered,
  })
  useStepTracking({
    currentStep: currentStepNumber,
    steps,
    enabled: isReady,
  })
  const { t } = useTranslation()

  const theme = useTheme()

  const {
    container,
    primaryText,
    title,
    locationText,
    headerWrapper,
    iconNavButton,
    playerControls,
    overviewWrapper,
    wrapper,
    highlightedText,
    stepTextContainer,
    stepProgressBar,
    stepDetailStack,
    venueLogowrapper,
    cooldownProgress,
  } = theme.components[`AppNavigationStep`]?.styleOverrides || {}
  const {
    root: topBarRoot,
    ordinalSelector,
    backButton,
  } = theme.components[`AppTopBar`]?.styleOverrides || {}

  const highlightedStepText = (
    <Typography variant="subTitle" component="span" sx={highlightedText} />
  )

  const handleSlideEnter = useCallback(
    () => !slideEntered && setSlideEntered(true),
    [slideEntered]
  )

  const verticalDirectionImageUrl = useMemo(() => {
    const src = _.get(
      theme?.assets?.verticalStep?.[currentVerticalStepMethod],
      "url",
      ""
    )
    return isValidURL(src) ? src : sanitizePublicPath(src)
  }, [currentVerticalStepMethod, theme?.assets?.verticalStep])

  const showVerticalStepDialog = !!verticalDirectionImageUrl

  const prevStepIcon = useMemo(
    () =>
      getIcon(theme?.assets?.navigationStep?.prevIcon) || (
        <PrevStepIcon fontSize="inherit" />
      ),
    [theme]
  )
  const nextStepIcon = useMemo(
    () =>
      getIcon(theme?.assets?.navigationStep?.nextIcon) || (
        <NextStepIcon fontSize="inherit" />
      ),
    [theme]
  )
  const playStepIcon = useMemo(
    () =>
      getIcon(theme?.assets?.palyStep?.nextIcon) || (
        <PlayArrowIcon fontSize="inherit" />
      ),
    [theme]
  )
  const playAgainStepIcon = useMemo(
    () =>
      getIcon(theme?.assets?.palyStep?.playAgainStepIcon) || (
        <RefreshArrowIcon fontSize="inherit" />
      ),
    [theme]
  )
  const pauseStepIcon = useMemo(
    () =>
      getIcon(theme?.assets?.palyStep?.pauseStepIcon) || (
        <PauseIcon fontSize="inherit" />
      ),
    [theme]
  )

  const backIcon = useMemo(
    () => getIcon(theme?.assets?.backIcon) || <BackIcon />,
    [theme]
  )

  const handleClickExit = useCallback(() => {
    navigate(directionOverviewPath, {
      replace: true,
    })
  }, [navigate, directionOverviewPath])

  const isRtl = theme.isRtl

  return (
    <>
      <VerticalDirectionImageDialog
        imageSrc={verticalDirectionImageUrl}
        description={currentStep?.description}
        originLevelName={currentOriginStepLevelName}
        destinationLevelName={currentDestStepLevelName}
        open={showVerticalStepDialog}
      />
      {/* Header Section */}
      <TopBarContainer sx={topBarRoot}>
        <Stack spacing={5}>
          {/* Back Button */}
          <Fab
            sx={backButton}
            size="medium"
            onClick={handleClickExit}
            className={isRtl ? "is-rtl" : ""}
          >
            {backIcon}
          </Fab>
          {/* only show ordinalSelector when there is no VerticalDirectionImageDialog showing */}
          {isMultiOrdinalVenue && !showVerticalStepDialog && (
            <Box sx={ordinalSelector}>
              <OrdinalSelector
                label="Floor"
                levels={currentLevel}
                selectedOrdinal={currentOrdinal}
              />
            </Box>
          )}
        </Stack>
      </TopBarContainer>
      {/* End of Header Section */}
      <Slide direction="up" in={true} onEnter={handleSlideEnter}>
        <Box sx={wrapper}>
          <Paper sx={container} ref={contentRef}>
            <Stack width="100%" direction="column" sx={stepDetailStack}>
              <Box sx={headerWrapper}>
                <Stack alignItems="center" spacing={1.5}>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <IconButton
                      id="prev-step-button"
                      data-testid="prev-step-button"
                      className={isRtl ? "is-rtl" : ""}
                      color="inherit"
                      sx={iconNavButton}
                      onClick={goToPrevStep}
                      disabled={!hasPrevStep}
                    >
                      {prevStepIcon}
                    </IconButton>
                    <Typography component={"h1"} textAlign="center" sx={title}>
                      {t("Process step", {
                        currentStep: currentStepNumber,
                        totalSteps: steps.length,
                      })}
                    </Typography>
                    <IconButton
                      id="next-step-button"
                      data-testid="next-step-button"
                      className={isRtl ? "is-rtl" : ""}
                      color="inherit"
                      sx={iconNavButton}
                      onClick={goToNextStep}
                      disabled={!hasNextStep}
                    >
                      {nextStepIcon}
                    </IconButton>
                  </Stack>
                  <Box sx={stepTextContainer}>
                    <Typography
                      variant="subTitle"
                      component="p"
                      sx={primaryText}
                    >
                      <Trans
                        components={{
                          category: highlightedStepText,
                          destination: highlightedStepText,
                          landmark: highlightedStepText,
                          intermediary: highlightedStepText,
                          toward: highlightedStepText,
                          turn: highlightedStepText,
                        }}
                      >
                        {t(currentStep?.description.text, {
                          category: t(
                            currentStep?.description.options?.category
                          ),
                          destination: t(
                            currentStep?.description.options?.destination
                          ),
                          landmark: t(
                            currentStep?.description.options?.landmark
                          ),
                          intermediary: t(
                            currentStep?.description.options?.intermediary
                          ),
                          toward: t(currentStep?.description.options?.toward),
                          turn: t(currentStep?.description.options?.turn),
                        })}
                      </Trans>
                    </Typography>
                  </Box>
                </Stack>
                <Box sx={playerControls}>
                  {isPlaying ? (
                    <IconButton
                      sx={iconNavButton}
                      onClick={pauseAutoPlay}
                      disabled={!isPlaying}
                    >
                      {pauseStepIcon}
                    </IconButton>
                  ) : (
                    <IconButton
                      sx={iconNavButton}
                      onClick={hasNextStep ? resumeAutoPlay : restartAutoPlay}
                    >
                      {hasNextStep ? playStepIcon : playAgainStepIcon}
                    </IconButton>
                  )}
                </Box>
              </Box>
              {/* mobile progress bar */}
              <StepProgressBar
                steps={steps}
                currentStep={currentStepNumber}
                sx={stepProgressBar}
                handleClickStep={goToStepByIndex}
                enableAnimateScroll={slideEntered && stepsAvailable}
              />
              {/* end of mobile progress bar */}
              {/* Location and overview section */}
              <Stack sx={overviewWrapper}>
                <Box>
                  <Box sx={venueLogowrapper}>
                    <CardMedia image={originVenueLogo} />
                  </Box>
                  <Typography
                    textAlign="start"
                    variant="caption"
                    sx={locationText}
                  >
                    {t(originName)}
                  </Typography>
                </Box>
                <Box>
                  <Box sx={venueLogowrapper}>
                    <CardMedia image={destinationVenueLogo} />
                  </Box>
                  <Typography
                    textAlign="end"
                    variant="caption"
                    sx={locationText}
                  >
                    {t(destinationName)}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
            {isPlaying && (
              <CoolDownProgress
                sx={cooldownProgress}
                duration={stepAutoplayConfig.delay}
                repeat={true}
                reverse={true}
              />
            )}
          </Paper>
        </Box>
      </Slide>
    </>
  )
}
